// Define API base URL based on environment
const getApiBaseUrl = (): string => {
  // For production builds
  if (import.meta.env.PROD) {
    // Use relative URL that works with same-origin deployment
    return '/api';
  }

  // For development
  return 'http://localhost:8080/api';
};

// Ensure endpoint starts with a slash
const formatEndpoint = (endpoint: string): string => {
  return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

// Export configuration
export const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  endpoints: {
    // Auth endpoints
    login: formatEndpoint('v1/auth/login'),
    refreshToken: formatEndpoint('v1/auth/refresh'),

    // Password reset endpoints
    passwordReset: {
      request: formatEndpoint('v1/auth/password-reset/request'),
      validate: formatEndpoint('v1/auth/password-reset/validate'),
      change: formatEndpoint('v1/auth/password-reset/change')
    },

    // User endpoints
    users: formatEndpoint('v1/users'),
    userRoles: (userId: string) => formatEndpoint(`v1/users/${userId}/roles`),

    // Role endpoints
    roles: {
      all: formatEndpoint('v1/roles'),
      assignable: formatEndpoint('v1/roles/assignable')
    },

    // Admin user management endpoints
    adminUsers: {
      suspend: (userId: string) =>
        formatEndpoint(`v1/admin/users/${userId}/suspend`),
      reactivate: (userId: string) =>
        formatEndpoint(`v1/admin/users/${userId}/reactivate`)
    },

    // User profile endpoints
    userProfile: {
      get: formatEndpoint('v1/users/profile'),
      update: formatEndpoint('v1/users/profile')
    },

    // User invitation endpoints
    invitations: {
      list: formatEndpoint('v1/invitations'),
      pending: formatEndpoint('v1/invitations/pending'),
      create: formatEndpoint('v1/invitations'),
      validate: (token: string) =>
        formatEndpoint(`v1/invitations/validate/${token}`),
      accept: formatEndpoint('v1/invitations/accept'),
      revoke: (id: string) => formatEndpoint(`v1/invitations/${id}`),
      resend: (id: string) => formatEndpoint(`v1/invitations/${id}/resend`)
    },

    // Disposition endpoints
    dispositions: {
      trackOccupations: formatEndpoint('v1/dispositions/track-occupations'),
      conflicts: formatEndpoint('v1/dispositions/track-occupations/conflicts'),
      base: formatEndpoint('v1/dispositions'),
      calendar: formatEndpoint('v1/dispositions/calendar'),
      changeTrack: (id: number) =>
        formatEndpoint(`v1/dispositions/track-occupations/${id}/change-track`)
    },

    // New endpoint for disposition/track-occupations (tracks + requests)
    dispositionTrackOccupations: formatEndpoint(
      'v1/disposition/track-occupations'
    ),

    // Route endpoints for journey progression
    routes: {
      base: formatEndpoint('v1/routes'),
      segments: formatEndpoint('v1/routes/segments')
    },

    // Orders endpoint for order status updates
    orders: formatEndpoint('v1/orders'),

    // Infrastructure endpoints
    portRailNets: formatEndpoint('v1/port-rail-nets'),

    // Registration endpoints
    registration: {
      ru: formatEndpoint('v1/register/ru'),
      admin: {
        pendingRegistrations: formatEndpoint('v1/admin/registrations/pending'),
        approveRegistration: (ruId: string) =>
          formatEndpoint(`v1/admin/registrations/${ruId}/approve`),
        rejectRegistration: (ruId: string) =>
          formatEndpoint(`v1/admin/registrations/${ruId}/reject`)
      }
    },
    infrastructure: {
      base: formatEndpoint('v1/infrastructure'), // GET (all), POST
      byId: (infrastructureId: number) =>
        formatEndpoint(`v1/infrastructure/${infrastructureId}`), // GET (one), PUT, DELETE
      connect: formatEndpoint('v1/infrastructure/tracks/connect') // POST
      // Removed tracks, switches, switchById as they don't exist
    },

    // Add other endpoints here as needed
    terminals: formatEndpoint('v1/terminals'),
    railwayStations: formatEndpoint('v1/railway-stations')
  },

  // Helper function to get full URL for an endpoint
  getFullUrl: (endpoint: string): string => {
    const formattedEndpoint = formatEndpoint(endpoint);
    const baseUrl = getApiBaseUrl();

    // Log the URL construction for debugging
    // console.log('API URL Construction:', {
    //   baseUrl,
    //   formattedEndpoint,
    //   fullUrl: `${baseUrl}${formattedEndpoint}`
    // });

    return `${baseUrl}${formattedEndpoint}`;
  }
};
